import React from 'react';
import {Box, Flex, Skeleton, Text, VStack} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {Unite} from 'api-types/ffconnect';
import {dateAvecSlashes, toDecimalFr} from 'ff-common';
import {PrixTag} from '../catalogue/components/PrixTag';
import {PrixDUneLivraison} from './usePrixHistoriqueArticle';

/**
 * Prix unitaires d'une livraison de l'historique d'achat, avec leur catégorie de tarif.
 * Rendu par le tooltip « 5 derniers achats » (lignes de grille) et par la modale « Historique »
 * (colonnes Prix et Catégorie), pour un commercial dont la société commerciale du client
 * autorise l'affichage des prix.
 */

type UnPrix = PrixDUneLivraison[number];

/**
 * « 18,37 € HT/kg », ou « 3 pièces à 17,00 € HT/kg » quand le jour a plusieurs prix.
 * La quantité est en unité de commande et le prix en unité de facturation : les deux
 * unités sont nommées, sinon « 3 × 17,00 € HT/kg » se lirait « 3 kg ».
 */
const TextePrix: React.FC<{
  prix: UnPrix;
  avecQuantite: boolean;
  uniteDeCommande: Unite;
  uniteDeFacturation: Unite;
}> = ({prix, avecQuantite, uniteDeCommande, uniteDeFacturation}) => {
  const {t} = useTranslation();
  // Préparer les valeurs pour l'affichage du prix avec ou sans quantité.
  const valeurs = {
    quantite: prix.quantite,
    uniteQuantite: t(`unite.${uniteDeCommande}`, {
      count: prix.quantite ?? 1,
      defaultValue: uniteDeCommande,
    }),
    prix: toDecimalFr(prix.prix_unitaire, 2, 3),
    unite: t(`unite.${uniteDeFacturation}`, {
      count: 1,
      defaultValue: uniteDeFacturation,
    }),
  };

  return (
    <Text
      fontSize='xs'
      color='gray.dark'
      whiteSpace='nowrap'
    >
      {avecQuantite && prix.quantite !== undefined
        ? t('historique_livraison.prix_unitaire_avec_quantite', valeurs)
        : t('historique_livraison.prix_unitaire', valeurs)}
    </Text>
  );
};

// Prix passés : une catégorie sans libellé affiche son code Tolede
const TagCategorieHistorique: React.FC<{prix: UnPrix}> = ({prix}) => (
  <PrixTag
    categorieDeTarif={prix.categorie}
    repliCategorieInconnue='code'
    styleProps={{py: 0}}
  />
);

interface HistoriqueAchatPrixProps {
  /** `undefined` pendant le chargement. */
  prix: PrixDUneLivraison | undefined;
  uniteDeCommande: Unite;
  uniteDeFacturation: Unite;
}

// Hauteur d'une ligne de prix dans la modale : identique dans les colonnes Prix et
// Catégorie, pour que chaque catégorie reste en face de son prix quand ils sont plusieurs
const HAUTEUR_LIGNE_PRIX = '20px';

/** Prix d'une livraison empilés, alignés à droite : colonne Prix de la modale. */
export const HistoriqueAchatPrix: React.FC<HistoriqueAchatPrixProps> = ({
  prix,
  uniteDeCommande,
  uniteDeFacturation,
}) => {
  if (!prix) {
    return (
      <Skeleton
        h='12px'
        w='90px'
        ml='auto'
      />
    );
  }

  return (
    <VStack
      spacing='0'
      alignItems='flex-end'
    >
      {prix.map((p) => (
        <Flex
          key={`${p.prix_unitaire}-${p.categorie}`}
          h={HAUTEUR_LIGNE_PRIX}
          align='center'
        >
          <TextePrix
            prix={p}
            avecQuantite={prix.length > 1}
            uniteDeCommande={uniteDeCommande}
            uniteDeFacturation={uniteDeFacturation}
          />
        </Flex>
      ))}
    </VStack>
  );
};

/** Catégories de tarif d'une livraison, en face de leur prix : colonne Catégorie de la modale. */
export const HistoriqueAchatCategories: React.FC<
  Pick<HistoriqueAchatPrixProps, 'prix'>
> = ({prix}) => {
  if (!prix) {
    return (
      <Skeleton
        h='12px'
        w='60px'
      />
    );
  }

  return (
    <VStack
      spacing='0'
      alignItems='flex-start'
    >
      {prix.map((p) => (
        <Flex
          key={`${p.prix_unitaire}-${p.categorie}`}
          h={HAUTEUR_LIGNE_PRIX}
          align='center'
        >
          <TagCategorieHistorique prix={p} />
        </Flex>
      ))}
    </VStack>
  );
};

/**
 * Une livraison en lignes d'une grille à quatre colonnes (date, quantité, prix, catégorie) :
 * tooltip « 5 derniers achats ». Plusieurs prix le même jour s'empilent dans les deux
 * dernières colonnes, la date et la quantité totale restant sur la première ligne.
 */
export const HistoriqueAchatLigneAvecPrix: React.FC<
  HistoriqueAchatPrixProps & {
    livraison_du: string;
    quantite: number;
  }
> = ({livraison_du, quantite, uniteDeCommande, prix, uniteDeFacturation}) => {
  const {t} = useTranslation();
  // Une ligne au moins, même sans prix (chargement ou livraison sans prix)
  const lignes: (UnPrix | undefined)[] = prix?.length ? prix : [undefined];

  return (
    <>
      {lignes.map((p, index) => (
        <React.Fragment key={p ? `${p.prix_unitaire}-${p.categorie}` : 'vide'}>
          <Text
            fontSize='xs'
            color='gray.dark'
            whiteSpace='nowrap'
          >
            {index === 0 && dateAvecSlashes(livraison_du)}
          </Text>
          <Text
            fontSize='xs'
            color='gray.dark'
            fontWeight='600'
            textAlign='right'
            whiteSpace='nowrap'
          >
            {index === 0 &&
              `${quantite} ${t(`unite.${uniteDeCommande}`, {count: quantite})}`}
          </Text>
          {p ? (
            <>
              <Box justifySelf='end'>
                <TextePrix
                  prix={p}
                  avecQuantite={lignes.length > 1}
                  uniteDeCommande={uniteDeCommande}
                  uniteDeFacturation={uniteDeFacturation}
                />
              </Box>
              {/* Cellule toujours présente : PrixTag ne rend rien sans catégorie,
                  et la grille décalerait alors toutes les cellules suivantes */}
              <Box>
                <TagCategorieHistorique prix={p} />
              </Box>
            </>
          ) : (
            <Box gridColumn='span 2'>
              {!prix && (
                <Skeleton
                  h='12px'
                  w='120px'
                  ml='auto'
                />
              )}
            </Box>
          )}
        </React.Fragment>
      ))}
    </>
  );
};
